import type { ComboboxItem, FilterFormValues } from "@/lib/form";
import { formatCnpjBasico, formatCnpjPartial, onlyDigits } from "@/lib/format";
import { BRAZIL_UFS, PORTE_LABELS, SITUACAO_LABELS, UI } from "@/lib/labels";
import type { StaticLookups } from "@/types/api";
import { Search, Share2, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { Combobox } from "./Combobox";
import { MultiSelect, type MultiSelectOption } from "./MultiSelect";
import { Button } from "./ui/button";
import { Chip } from "./ui/chip";
import { Field } from "./ui/field";
import { Input } from "./ui/input";
import { Spinner } from "./ui/spinner";
import { Toggle } from "./ui/toggle";

interface FilterFormProps {
  values: FilterFormValues;
  setValues: Dispatch<SetStateAction<FilterFormValues>>;
  staticLookups: StaticLookups | null;
  municipios: MultiSelectOption[];
  municipiosLoading: boolean;
  cnaeSearch: (query: string, signal: AbortSignal) => Promise<ComboboxItem[]>;
  naturezaSearch: (query: string, signal: AbortSignal) => Promise<ComboboxItem[]>;
  onSubmit: () => void;
  onClear: () => void;
  onSaveShare: () => void;
  searching: boolean;
  activeFilterCount: number;
}

const sectionTitleClass = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
const CNPJ_HINT = "Pesquisa por CNPJ ignora os demais filtros (chave única)";
const SOCIO_HINT = "Pesquisa por sócio ignora os demais filtros (os resultados são outros)";
const SOCIO_INPUT_HINT =
  "Nome exato do sócio (como consta na Receita) ou CNPJ do sócio pessoa jurídica";
const DISABLED_BY_CNPJ = "Desabilitado pela busca por CNPJ";
const DISABLED_BY_SOCIO = "Desabilitado pela busca por sócio";

/** Formulário de filtros da pesquisa avançada (espelha a tela de referência). */
export function FilterForm({
  values,
  setValues,
  staticLookups,
  municipios,
  municipiosLoading,
  cnaeSearch,
  naturezaSearch,
  onSubmit,
  onClear,
  onSaveShare,
  searching,
  activeFilterCount,
}: FilterFormProps) {
  const update = useCallback(
    <K extends keyof FilterFormValues>(key: K, value: FilterFormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }) as FilterFormValues);
    },
    [setValues],
  );

  const situacaoOptions = useMemo<MultiSelectOption[]>(() => {
    if (staticLookups && staticLookups.situacoes.length > 0) {
      return staticLookups.situacoes.map((item) => ({
        value: String(item.codigo),
        label: item.label,
      }));
    }
    return Object.entries(SITUACAO_LABELS).map(([codigo, label]) => ({ value: codigo, label }));
  }, [staticLookups]);

  const porteOptions = useMemo<MultiSelectOption[]>(() => {
    if (staticLookups && staticLookups.portes.length > 0) {
      return staticLookups.portes.map((item) => ({
        value: String(item.codigo),
        label: item.label,
      }));
    }
    return Object.entries(PORTE_LABELS).map(([codigo, label]) => ({ value: codigo, label }));
  }, [staticLookups]);

  const ufOptions = useMemo<MultiSelectOption[]>(() => {
    const ufs = staticLookups && staticLookups.ufs.length > 0 ? staticLookups.ufs : BRAZIL_UFS;
    return ufs.map((uf) => ({ value: uf, label: uf }));
  }, [staticLookups]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  // Duas "chaves" de busca que ignoram os demais filtros e se excluem entre si:
  // CNPJ (empresa) e Sócio (pessoa → empresas, na rota /socio). Qualquer uma
  // preenchida trava todos os filtros de empresa (`lockOthers`).
  const cnpjDigits = values.cnpjInput;
  const cnpjActive = cnpjDigits.length > 0;
  const cnpjError =
    cnpjActive && cnpjDigits.length !== 8 && cnpjDigits.length !== 14
      ? "CNPJ incompleto (informe 8 ou 14 dígitos)"
      : undefined;

  const socioTrim = values.socioInput.trim();
  const socioActive = socioTrim.length > 0;
  const socioIsPj = onlyDigits(socioTrim).length === 14;
  const socioError =
    socioActive && !socioIsPj && [...socioTrim].length < 3
      ? "Informe o nome completo do sócio (mín. 3 caracteres) ou o CNPJ do sócio (14 dígitos)"
      : undefined;

  const lockOthers = cnpjActive || socioActive;
  const lockHint = socioActive ? DISABLED_BY_SOCIO : cnpjActive ? DISABLED_BY_CNPJ : undefined;

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6"
    >
      {/* Busca por sócio — modo alternativo (pessoa → empresas, rota /socio).
          Fica no topo, em destaque: quando preenchido, trava todo o resto. */}
      <p className={sectionTitleClass}>Busca por sócio</p>
      <div className="mt-3">
        <Field
          label={UI.filters.socio}
          htmlFor="filter-socio"
          error={socioError}
          hint={cnpjActive ? DISABLED_BY_CNPJ : socioActive ? SOCIO_HINT : SOCIO_INPUT_HINT}
        >
          <Input
            id="filter-socio"
            value={values.socioInput}
            autoComplete="off"
            disabled={cnpjActive}
            onChange={(event) => update("socioInput", event.target.value)}
            placeholder="Nome exato do sócio ou CNPJ (sócio PJ)"
          />
        </Field>
      </div>

      {/* Linha 1 — identificação e atividade */}
      <p className={`${sectionTitleClass} mt-6`}>Empresa e atividade</p>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label={UI.filters.termo}
          htmlFor="filter-termo"
          hint={lockOthers ? lockHint : undefined}
        >
          <Input
            id="filter-termo"
            value={values.termo}
            disabled={lockOthers}
            onChange={(event) => update("termo", event.target.value)}
            placeholder="Razão Social ou Fantasia"
          />
        </Field>

        <Field
          label="CNPJ"
          htmlFor="filter-cnpj"
          error={cnpjError}
          hint={socioActive ? DISABLED_BY_SOCIO : cnpjActive ? CNPJ_HINT : undefined}
        >
          <Input
            id="filter-cnpj"
            value={formatCnpjPartial(values.cnpjInput)}
            inputMode="numeric"
            autoComplete="off"
            disabled={socioActive}
            onChange={(event) => update("cnpjInput", onlyDigits(event.target.value).slice(0, 14))}
            placeholder="00.000.000/0000-00"
          />
          {cnpjDigits.length === 8 ? (
            <div className="flex flex-wrap gap-1.5">
              <Chip
                label={`Raiz do CNPJ: ${formatCnpjBasico(cnpjDigits)}`}
                onRemove={() => update("cnpjInput", "")}
              />
            </div>
          ) : null}
        </Field>

        <div className="flex flex-col gap-1.5">
          <Field label={UI.filters.cnae} htmlFor="filter-cnae">
            <Combobox
              id="filter-cnae"
              placeholder="Buscar por código ou nome…"
              selected={values.cnaeItems}
              onChange={(items) => update("cnaeItems", items)}
              search={cnaeSearch}
              disabled={lockOthers}
            />
          </Field>
          <Toggle
            label={UI.filters.incluirCnaeSecundaria}
            checked={values.incluirCnaeSecundaria}
            onChange={(checked) => update("incluirCnaeSecundaria", checked)}
            disabled={lockOthers}
          />
        </div>

        <Field label={UI.filters.natureza} htmlFor="filter-natureza">
          <Combobox
            id="filter-natureza"
            placeholder="Buscar por código ou nome…"
            selected={values.naturezaItems}
            onChange={(items) => update("naturezaItems", items)}
            search={naturezaSearch}
            disabled={lockOthers}
          />
        </Field>
      </div>

      {/* Linha 2 — localização e classificação */}
      <p className={`${sectionTitleClass} mt-6`}>Localização e classificação</p>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={UI.filters.situacao} htmlFor="filter-situacao">
          <MultiSelect
            id="filter-situacao"
            placeholder="Todas as situações"
            ariaLabel={UI.filters.situacao}
            options={situacaoOptions}
            selected={values.situacao}
            onChange={(vals) => update("situacao", vals)}
            disabled={lockOthers}
          />
        </Field>

        <Field label={UI.filters.uf} htmlFor="filter-uf">
          <MultiSelect
            id="filter-uf"
            placeholder="Todos os estados"
            ariaLabel={UI.filters.uf}
            options={ufOptions}
            selected={values.uf}
            onChange={(vals) => update("uf", vals)}
            searchable
            searchPlaceholder="Filtrar UF…"
            disabled={lockOthers}
          />
        </Field>

        <Field label={UI.filters.municipio} htmlFor="filter-municipio">
          <MultiSelect
            id="filter-municipio"
            placeholder={municipiosLoading ? "Carregando municípios…" : "Todos os municípios"}
            ariaLabel={UI.filters.municipio}
            options={municipios}
            selected={values.municipio}
            onChange={(vals) => update("municipio", vals)}
            searchable
            searchPlaceholder="Filtrar município…"
            disabled={lockOthers || values.uf.length === 0}
            disabledHint={
              socioActive
                ? "Indisponível na busca por sócio"
                : cnpjActive
                  ? "Indisponível na busca por CNPJ"
                  : "Selecione ao menos uma UF"
            }
          />
        </Field>

        <Field label={UI.filters.bairro} htmlFor="filter-bairro">
          <Input
            id="filter-bairro"
            value={values.bairro}
            disabled={lockOthers}
            onChange={(event) => update("bairro", event.target.value)}
            placeholder="Ex.: Centro"
          />
        </Field>

        <Field label={UI.filters.cep} htmlFor="filter-cep">
          <Input
            id="filter-cep"
            value={values.cep}
            inputMode="numeric"
            maxLength={8}
            disabled={lockOthers}
            onChange={(event) => update("cep", event.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="Somente números"
          />
        </Field>

        <Field label={UI.filters.porte} htmlFor="filter-porte">
          <MultiSelect
            id="filter-porte"
            placeholder="Todos os portes"
            ariaLabel={UI.filters.porte}
            options={porteOptions}
            selected={values.porte}
            onChange={(vals) => update("porte", vals)}
            disabled={lockOthers}
          />
        </Field>
      </div>

      {/* Linha 3 — contato, datas e capital */}
      <p className={`${sectionTitleClass} mt-6`}>Contato, abertura e capital</p>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Field label={UI.filters.ddd} htmlFor="filter-ddd">
          <Input
            id="filter-ddd"
            value={values.ddd}
            inputMode="numeric"
            maxLength={2}
            disabled={lockOthers}
            onChange={(event) => update("ddd", event.target.value.replace(/\D/g, "").slice(0, 2))}
            placeholder="Ex.: 11"
          />
        </Field>

        <Field label={UI.filters.aberturaDe} htmlFor="filter-abertura-de">
          <Input
            id="filter-abertura-de"
            type="date"
            value={values.aberturaDe}
            disabled={lockOthers}
            onChange={(event) => update("aberturaDe", event.target.value)}
          />
        </Field>

        <Field label={UI.filters.aberturaAte} htmlFor="filter-abertura-ate">
          <Input
            id="filter-abertura-ate"
            type="date"
            value={values.aberturaAte}
            disabled={lockOthers}
            onChange={(event) => update("aberturaAte", event.target.value)}
          />
        </Field>

        <Field label={UI.filters.capitalDe} htmlFor="filter-capital-de">
          <Input
            id="filter-capital-de"
            inputMode="decimal"
            value={values.capitalDe}
            disabled={lockOthers}
            onChange={(event) => update("capitalDe", event.target.value)}
            placeholder="R$ mínimo"
          />
        </Field>

        <Field label={UI.filters.capitalAte} htmlFor="filter-capital-ate">
          <Input
            id="filter-capital-ate"
            inputMode="decimal"
            value={values.capitalAte}
            disabled={lockOthers}
            onChange={(event) => update("capitalAte", event.target.value)}
            placeholder="R$ máximo"
          />
        </Field>
      </div>

      {/* Linha 4 — refinamentos (toggles) */}
      <p className={`${sectionTitleClass} mt-6`}>Refinamentos</p>
      <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-4">
        <Toggle
          label={UI.filters.somenteMei}
          checked={values.somenteMei}
          onChange={(checked) => update("somenteMei", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.excluirMei}
          checked={values.excluirMei}
          onChange={(checked) => update("excluirMei", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.somenteMatriz}
          checked={values.somenteMatriz}
          onChange={(checked) => update("somenteMatriz", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.somenteFilial}
          checked={values.somenteFilial}
          onChange={(checked) => update("somenteFilial", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.comTelefone}
          checked={values.comTelefone}
          onChange={(checked) => update("comTelefone", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.somenteFixo}
          checked={values.somenteFixo}
          onChange={(checked) => update("somenteFixo", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.somenteCelular}
          checked={values.somenteCelular}
          onChange={(checked) => update("somenteCelular", checked)}
          disabled={lockOthers}
        />
        <Toggle
          label={UI.filters.comEmail}
          checked={values.comEmail}
          onChange={(checked) => update("comEmail", checked)}
          disabled={lockOthers}
        />
      </div>

      {/* Ações */}
      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <Button type="submit" size="lg" disabled={searching}>
          {searching ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Search className="h-4 w-4" aria-hidden="true" />
          )}
          {UI.actions.search}
        </Button>
        <Button type="button" variant="outline" size="lg" onClick={onSaveShare}>
          <Share2 className="h-4 w-4" aria-hidden="true" />
          {UI.actions.saveShare}
        </Button>
        {activeFilterCount > 0 || cnpjActive || socioActive ? (
          <Button type="button" variant="ghost" size="lg" onClick={onClear}>
            <X className="h-4 w-4" aria-hidden="true" />
            {UI.actions.clear}
            {activeFilterCount > 0 ? (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-xs text-muted-foreground">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
